import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Scanner de code-barres.
 *
 * POURQUOI UN DÉCODEUR WASM, ET PAS L'API DU NAVIGATEUR
 *
 * `BarcodeDetector` n'existe pas dans Safari — ni sur macOS, ni sur iOS, et
 * c'est « under consideration » depuis 2024. Or tout le foyer est sur
 * iPhone : il n'y a donc AUCUN chemin natif ici, le WASM n'est pas un repli
 * mais la seule voie.
 *
 * Il est chargé en LAZY, à l'ouverture du scanner uniquement — jamais dans
 * le bundle initial. C'est ~2 Mo qu'on ne veut ni au premier rendu, ni dans
 * le précache du service worker ; le navigateur le gardera en cache HTTP
 * après le premier scan.
 *
 * `BarcodeDetector` reste testé en premier : sur l'Android d'un invité, il
 * évite le téléchargement.
 *
 * ⚠ RENDU PAR PORTAIL, ET C'EST INDISPENSABLE.
 *
 * Le scanner est ouvert depuis la feuille d'ajout rapide. Or cette feuille
 * porte un `transform` (son glissement), et un ancêtre transformé devient le
 * bloc conteneur de tout `position: fixed` qu'il englobe — la règle vaut
 * aussi pour `filter`, `perspective` et `will-change`. Rendu dans l'arbre de
 * la feuille, un `fixed inset-0` se calait donc sur la feuille et non sur
 * l'écran : l'aperçu caméra apparaissait dans une bande en bas, rogné par
 * l'`overflow-hidden` de la feuille par-dessus le marché.
 *
 * Le portail vers `document.body` sort de ce contexte. C'est la seule
 * correction qui tienne — élever le z-index n'y change rien, le problème
 * n'est pas l'empilement mais le référentiel.
 */
/**
 * Ce que l'appelant répond après avoir traité un code.
 *
 * `ajoute` → on reste dans la caméra et on enchaîne. `inconnu` → il faut un
 * nom, donc on sort : c'est le seul cas qui demande le clavier.
 */
export type Resultat =
  | { statut: 'ajoute'; nom: string }
  | { statut: 'deja'; nom: string }
  | { statut: 'inconnu' }

export function Scanner({
  ouvert,
  onCode,
  onFermer,
  destination,
}: {
  ouvert: boolean
  onCode: (ean: string) => Promise<Resultat>
  onFermer: (nbAjoutes: number) => void
  destination: string
}) {
  const video = useRef<HTMLVideoElement>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [chargement, setChargement] = useState(true)
  const [ajoutes, setAjoutes] = useState<Array<{ id: string; nom: string; deja: boolean }>>([])
  const [flash, setFlash] = useState(false)
  /*
   * Codes déjà lus pendant CETTE session de scan, avec l'instant de lecture.
   *
   * Sans ce garde-fou, la boucle relit le même code sept fois par seconde
   * tant que le produit reste devant l'objectif : on ajouterait le même
   * article en boucle. Six secondes laissent le temps de retourner un
   * produit sans le re-scanner, tout en permettant de rescanner volontairement
   * un article identique plus tard dans le sac.
   */
  const vus = useRef(new Map<string, number>())
  const enTraitement = useRef(false)

  // Miroir synchrone de `ajoutes` : la boucle de scan tourne hors du cycle
  // de rendu et ne verrait sinon qu'un état figé au moment du montage.
  const ajoutesRef = useRef<Array<{ id: string; nom: string; deja: boolean }>>([])

  /*
   * `onCode` et `onFermer` passent par des refs, et l'effet ne dépend QUE de
   * `ouvert`.
   *
   * Sans ça, l'effet se relance à chaque rendu du parent — et le parent se
   * rend à chaque changement du magasin, donc après CHAQUE produit scanné.
   * Conséquence : le flux caméra était coupé et rouvert entre deux produits,
   * le décodeur réinitialisé, et l'état d'affichage perdu. L'enchaînement
   * fonctionnait côté données et ne se voyait pas à l'écran.
   */
  const onCodeRef = useRef(onCode)
  const onFermerRef = useRef(onFermer)
  onCodeRef.current = onCode
  onFermerRef.current = onFermer

  useEffect(() => {
    if (!ouvert) {
      vus.current.clear()
      setAjoutes([])
      ajoutesRef.current = []
      return
    }
    let flux: MediaStream | null = null
    let actif = true
    let minuterie: ReturnType<typeof setTimeout> | null = null

    async function demarrer() {
      try {
        flux = await navigator.mediaDevices.getUserMedia({
          // `environment` : la caméra arrière. Sans ça, iOS ouvre la frontale.
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
        })
        if (!actif) return flux.getTracks().forEach((t) => t.stop())
        if (video.current) {
          video.current.srcObject = flux
          await video.current.play().catch(() => {})
        }

        const lire = await construireLecteur()
        setChargement(false)

        const boucle = async () => {
          if (!actif || !video.current) return

          if (!enTraitement.current) {
            const code = await lire(video.current).catch(() => null)
            const vuLe = code ? vus.current.get(code) : undefined
            const frais = !vuLe || Date.now() - vuLe > 6000

            if (code && frais) {
              vus.current.set(code, Date.now())
              enTraitement.current = true
              navigator.vibrate?.(12)
              setFlash(true)
              setTimeout(() => setFlash(false), 180)

              try {
                const r = await onCodeRef.current(code)
                if (!actif) return
                if (r.statut === 'inconnu') {
                  // Seul cas qui demande le clavier : on sort.
                  onFermerRef.current(ajoutesRef.current.length)
                  return
                }
                setAjoutes((a) => {
                  const suite = [
                    { id: crypto.randomUUID(), nom: r.nom, deja: r.statut === 'deja' },
                    ...a,
                  ].slice(0, 4)
                  ajoutesRef.current = suite
                  return suite
                })
              } finally {
                enTraitement.current = false
              }
            }
          }

          // ~7 images/seconde : suffisant pour un code-barres qu'on présente,
          // et bien plus économe en batterie qu'une boucle par frame.
          minuterie = setTimeout(boucle, 140)
        }
        void boucle()
      } catch (e) {
        setChargement(false)
        setErreur(
          e instanceof DOMException && e.name === 'NotAllowedError'
            ? "L'accès à la caméra a été refusé."
            : "La caméra n'est pas disponible.",
        )
      }
    }

    void demarrer()
    return () => {
      actif = false
      if (minuterie) clearTimeout(minuterie)
      flux?.getTracks().forEach((t) => t.stop())
    }
  }, [ouvert])

  if (!ouvert) return null

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black">
      {/*
        `absolute inset-0` plutôt qu'un flex : la vidéo doit occuper la
        TOTALITÉ de l'écran, sans laisser de bandes noires. object-cover
        rogne le surplus, ce qui est le bon compromis — on cadre soi-même en
        approchant le téléphone.
      */}
      <video
        ref={video}
        playsInline
        muted
        autoPlay
        className="absolute inset-0 size-full object-cover"
        aria-label="Vue de la caméra"
      />

      {/*
        Repère de visée : une fenêtre claire dans un voile sombre. Le voile
        fait deux choses — il dit où viser sans tracer de cadre par-dessus
        l'image, et il rend le texte blanc lisible quel que soit ce que
        filme la caméra.
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'linear-gradient(to bottom, rgba(0,0,0,.55) 0%, rgba(0,0,0,.55) 34%, transparent 34%, transparent 58%, rgba(0,0,0,.55) 58%, rgba(0,0,0,.55) 100%)',
        }}
      />
      <div className="pointer-events-none absolute inset-x-6 top-[34%] h-[24%] rounded-lg border-2 border-white/80" />

      {/* Éclair de confirmation : la caméra ne s'arrête pas, il faut donc un
          retour visuel immédiat que le code a bien été lu. */}
      {/*
        L'élément est TOUJOURS monté, seule son opacité change. La version
        précédente le montait et le démontait : la transition n'avait alors
        aucune valeur de départ et ne jouait jamais — c'était une coupe
        sèche, malgré la déclaration de transition.

        Sortie plus rapide que l'entrée (90 ms contre 40) : l'éclair doit
        confirmer immédiatement, puis s'effacer sans traîner sur l'image.
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-6 top-[34%] h-[24%] rounded-lg bg-white/35"
        style={{
          opacity: flash ? 1 : 0,
          transition: flash
            ? 'opacity 40ms linear'
            : 'opacity 90ms var(--ease-out)',
        }}
      />

      <p
        className="pointer-events-none absolute inset-x-8 top-[60%] text-center text-body text-white"
        role="status"
      >
        {erreur ??
          (chargement
            ? 'Préparation du lecteur…'
            : ajoutes.length === 0
              ? 'Présente le code-barres dans le cadre'
              : 'Passe au produit suivant')}
      </p>

      {/*
        Les derniers ajouts, empilés sous le cadre. C'est ce qui rend
        l'enchaînement utilisable : sans retour, on ne sait plus lequel on a
        déjà passé, et on rescanne par doute.
      */}
      {ajoutes.length > 0 && (
        <ul className="pointer-events-none absolute inset-x-6 top-[66%] flex flex-col gap-1.5">
          {ajoutes.map((a, i) => (
            <li
              key={a.id}
              className="flex items-center justify-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-meta text-white"
              style={{
                // Les précédents s'estompent : le dernier lu est celui qui
                // compte, les autres ne sont qu'un fil d'Ariane.
                opacity: 1 - i * 0.22,
                animation: i === 0 ? 'pop 180ms var(--ease-out)' : undefined,
              }}
            >
              {/* SVG et non « ✓ » : Inter ne contient pas ce glyphe et rendait
                  un carré vide. Même écueil que le « ＋ » pleine chasse. */}
              <svg aria-hidden viewBox="0 0 24 24" className="size-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
                {a.deja ? <path d="M12 19V5M5 12l7-7 7 7" /> : <path d="M20 6 9 17l-5-5" />}
              </svg>
              <span className="truncate">{a.nom}</span>
              {a.deja && <span className="shrink-0 opacity-70">déjà là</span>}
            </li>
          ))}
        </ul>
      )}

      <div
        className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-3 px-6"
        style={{ paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))' }}
      >
        <button
          type="button"
          onClick={() => onFermer(ajoutes.length)}
          className="pressable h-[52px] min-w-[180px] rounded-sm bg-white px-8 text-head text-ink"
        >
          {erreur
            ? 'Saisir à la main'
            : ajoutes.length > 0
              ? `Terminé · ${ajoutes.length}`
              : 'Annuler'}
        </button>
      </div>

      {/* Rappel de destination : on enchaîne dix produits, il faut pouvoir
          vérifier d'un coup d'œil qu'ils vont au bon endroit. */}
      <p
        className="pointer-events-none absolute inset-x-0 text-center text-meta text-white/70"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      >
        → {destination}
      </p>

    </div>,
    document.body,
  )
}

type Lecteur = (v: HTMLVideoElement) => Promise<string | null>

/**
 * Construit le lecteur : natif si le navigateur sait faire, WASM sinon.
 *
 * L'import de zxing-wasm est DYNAMIQUE : sur un navigateur qui a
 * `BarcodeDetector`, le module n'est jamais téléchargé.
 */
async function construireLecteur(): Promise<Lecteur> {
  const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128'] as const

  const Detecteur = (globalThis as { BarcodeDetector?: new (o: unknown) => unknown })
    .BarcodeDetector
  if (Detecteur) {
    const d = new Detecteur({ formats: FORMATS }) as {
      detect: (s: CanvasImageSource) => Promise<Array<{ rawValue: string }>>
    }
    return async (v) => (await d.detect(v))[0]?.rawValue ?? null
  }

  const { readBarcodesFromImageData, prepareZXingModule } = await import('zxing-wasm/reader')

  /*
   * ON AUTO-HÉBERGE LE .wasm.
   *
   * Par défaut, zxing-wasm surcharge `locateFile` pour aller le chercher sur
   * le CDN jsDelivr. C'est inacceptable ici pour deux raisons : la règle
   * « aucun CDN en production », et surtout le fait qu'une application
   * offline-first ne peut pas dépendre d'un tiers pour fonctionner — le
   * scanner tomberait en panne exactement le jour où jsDelivr tousse.
   *
   * `__URL_WASM_ZXING__` est injecté au build par le plugin
   * `popote-wasm-local` (vite.config.ts), qui copie le binaire dans la sortie
   * avec la version dans son nom. Il est donc servi depuis notre domaine, et
   * le cache HTTP le garde après le premier scan.
   */
  await prepareZXingModule({
    overrides: { locateFile: () => __URL_WASM_ZXING__ },
    fireImmediately: true,
  })

  const toile = document.createElement('canvas')
  const ctx = toile.getContext('2d', { willReadFrequently: true })!

  return async (v) => {
    if (!v.videoWidth) return null
    /*
     * On ne décode qu'une BANDE CENTRALE, celle du repère de visée. Deux
     * bénéfices : c'est trois fois plus rapide qu'une image complète, et ça
     * évite de lire par accident un code-barres voisin sur l'étagère.
     */
    const l = Math.min(v.videoWidth, 720)
    const h = Math.round(l * 0.35)
    toile.width = l
    toile.height = h
    ctx.drawImage(
      v,
      (v.videoWidth - l) / 2,
      (v.videoHeight - h) / 2,
      l,
      h,
      0,
      0,
      l,
      h,
    )
    const resultats = await readBarcodesFromImageData(ctx.getImageData(0, 0, l, h), {
      formats: ['EAN-13', 'EAN-8', 'UPC-A', 'UPC-E', 'Code128'],
      tryHarder: false,
    })
    return resultats[0]?.text ?? null
  }
}
